"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from "react";
import type { TastytradeAccount } from "@/types/tastytrade";

type SelectionMode = "single" | "multiple";

const PRIMARY_ACCOUNT_NUMBERS: string[] = ["5WX84566", "5WY61153"];

// The owner's primary trading account. When there is no previously-saved
// selection, this account is selected by default (never broker list order).
export const DEFAULT_ACCOUNT_NUMBER = "5WX84566";

interface AccountSelectionContextType {
  mode: SelectionMode;
  selectedAccounts: TastytradeAccount[];
  primaryAccountNumbers: string[]; // Your funded accounts
  setMode: (mode: SelectionMode) => void;
  toggleAccount: (account: TastytradeAccount) => void;
  selectAccount: (account: TastytradeAccount) => void;
  selectAccounts: (accounts: TastytradeAccount[]) => void;
  clearSelection: () => void;
  isAccountSelected: (accountNumber: string) => boolean;
}

// Pick the account selected when no saved selection exists: the owner's
// primary account (DEFAULT_ACCOUNT_NUMBER) if present, otherwise any funded
// primary, otherwise the first account. Never depends on broker list order
// for the primary pick.
function pickDefaultAccount(
  accounts: TastytradeAccount[],
): TastytradeAccount | undefined {
  return (
    accounts.find(
      (acc) => acc?.account?.["account-number"] === DEFAULT_ACCOUNT_NUMBER,
    ) ??
    accounts.find(
      (acc) =>
        acc?.account &&
        PRIMARY_ACCOUNT_NUMBERS.includes(acc.account["account-number"]),
    ) ??
    accounts.find((acc) => acc?.account)
  );
}

const AccountSelectionContext = createContext<
  AccountSelectionContextType | undefined
>(undefined);

export function useAccountSelection() {
  const context = useContext(AccountSelectionContext);
  if (!context) {
    throw new Error(
      "useAccountSelection must be used within an AccountSelectionProvider",
    );
  }
  return context;
}

interface AccountSelectionProviderProps {
  children: ReactNode;
  accounts: TastytradeAccount[];
}

export function AccountSelectionProvider({
  children,
  accounts = [],
}: AccountSelectionProviderProps) {
  const [mode, setMode] = useState<SelectionMode>("multiple");
  const [selectedAccounts, setSelectedAccounts] = useState<TastytradeAccount[]>(
    [],
  );

  // Your funded accounts
  const primaryAccountNumbers = useMemo(
    () => Array.from(PRIMARY_ACCOUNT_NUMBERS),
    [],
  );

  // Load saved selection from localStorage on mount
  useEffect(() => {
    const savedMode = localStorage.getItem(
      "account_selection_mode",
    ) as SelectionMode;
    const savedAccountNumbers = localStorage.getItem(
      "selected_account_numbers",
    );

    if (savedMode) {
      setMode(savedMode);
    }

    if (savedAccountNumbers && accounts && accounts.length > 0) {
      const accountNumbers = JSON.parse(savedAccountNumbers) as string[];
      const savedAccounts = accounts.filter(
        (acc) =>
          acc?.account &&
          accountNumbers.includes(acc.account["account-number"]),
      );
      if (savedAccounts.length > 0) {
        setSelectedAccounts(savedAccounts);
      } else {
        const defaultAccount = pickDefaultAccount(accounts);
        if (defaultAccount) {
          setSelectedAccounts([defaultAccount]);
        }
      }
    } else if (accounts && accounts.length > 0) {
      // No saved selection: auto-select the owner's default account
      const defaultAccount = pickDefaultAccount(accounts);
      if (defaultAccount) {
        setSelectedAccounts([defaultAccount]);
      }
    }
  }, [accounts, primaryAccountNumbers]);

  // Save selection to localStorage when it changes
  useEffect(() => {
    if (selectedAccounts.length > 0) {
      const accountNumbers = selectedAccounts
        .map((acc) => acc?.account?.["account-number"])
        .filter(Boolean);
      localStorage.setItem(
        "selected_account_numbers",
        JSON.stringify(accountNumbers),
      );
    }
    localStorage.setItem("account_selection_mode", mode);
  }, [selectedAccounts, mode]);

  const toggleAccount = useCallback((account: TastytradeAccount) => {
    if (mode === "single") {
      setSelectedAccounts([account]);
    } else {
      setSelectedAccounts((prev) => {
        const isSelected = prev.some(
          (acc) =>
            acc?.account?.["account-number"] ===
            account?.account?.["account-number"],
        );

        if (isSelected) {
          return prev.filter(
            (acc) =>
              acc?.account?.["account-number"] !==
              account?.account?.["account-number"],
          );
        } else {
          return [...prev, account];
        }
      });
    }
  }, [mode]);

  const selectAccount = useCallback((account: TastytradeAccount) => {
    setSelectedAccounts([account]);
    if (mode === "multiple") {
      setMode("single");
    }
  }, [mode]);

  const selectAccounts = useCallback((accounts: TastytradeAccount[]) => {
    setSelectedAccounts(accounts);
    if (accounts.length > 1 && mode === "single") {
      setMode("multiple");
    }
  }, [mode]);

  const clearSelection = useCallback(() => {
    setSelectedAccounts([]);
  }, []);

  const isAccountSelected = useCallback((accountNumber: string) => {
    return selectedAccounts.some(
      (acc) => acc?.account?.["account-number"] === accountNumber,
    );
  }, [selectedAccounts]);

  const value = useMemo<AccountSelectionContextType>(
    () => ({
      mode,
      selectedAccounts,
      primaryAccountNumbers,
      setMode,
      toggleAccount,
      selectAccount,
      selectAccounts,
      clearSelection,
      isAccountSelected,
    }),
    [
      mode,
      selectedAccounts,
      primaryAccountNumbers,
      toggleAccount,
      selectAccount,
      selectAccounts,
      clearSelection,
      isAccountSelected,
    ],
  );

  return (
    <AccountSelectionContext.Provider value={value}>
      {children}
    </AccountSelectionContext.Provider>
  );
}
